import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, map } from 'rxjs';
import { PatientService } from './patient.service';

export type SpecialCategoryTagItem = {
  uid: string;
  label: string;
  alarm: boolean;
  warning: boolean;
  passive: boolean;
};

export type SpecialCategoryTagLogFieldKey = 'label' | 'alarm' | 'passive';

export type SpecialCategoryTagLogFieldChange = {
  field: SpecialCategoryTagLogFieldKey;
  label: string;
  icon: string;
  oldText: string;
  newText: string;
};

export type SpecialCategoryTagLogView = {
  title: string;
  action: string;
  date: any;
  userName: string;
  badgeText: string;
  typeClass: 'created' | 'updated' | 'deleted';
  icon: string;
  changedFields: SpecialCategoryTagLogFieldChange[];
  raw: any;
};

@Injectable({
  providedIn: 'root',
})
export class PatientSpecialCategoryTagsService {
  private readonly fieldOrder: SpecialCategoryTagLogFieldKey[] = [
    'label',
    'alarm',
    'passive',
  ];

  private readonly fieldLabels: Record<SpecialCategoryTagLogFieldKey, string> = {
    label: 'Etiket',
    alarm: 'Alarm',
    passive: 'Pasif',
  };

  private readonly fieldIcons: Record<SpecialCategoryTagLogFieldKey, string> = {
    label: 'fa-tag',
    alarm: 'fa-bell',
    passive: 'fa-ban',
  };

  constructor(private readonly patientService: PatientService) { }

  getItems(form: FormGroup): SpecialCategoryTagItem[] {
    const tags = form.get('specialCategoryTags')?.value || [];

    return this.sortTags(tags);
  }

  normalizeTag(item: any): SpecialCategoryTagItem {
    const parsedItem = this.parseMaybeJson(item);

    return {
      uid: parsedItem?.uid || parsedItem?.id || this.createUid(),
      label: parsedItem?.label || parsedItem?.name || parsedItem?.tag || '',
      alarm: !!parsedItem?.alarm,
      warning: !!parsedItem?.warning,
      passive: !!parsedItem?.passive,
    };
  }

  addTag(form: FormGroup): boolean {
    const tags = this.getItems(form);

    if (this.hasEmptyTag(tags)) {
      this.touchControl(form);
      return false;
    }

    tags.push({
      uid: this.createUid(),
      label: '',
      alarm: false,
      warning: false,
      passive: false,
    });

    this.setItems(form, tags);
    return true;
  }

  updateTag(
    form: FormGroup,
    index: number,
    field: keyof SpecialCategoryTagItem,
    value: string | boolean
  ): void {
    const tags = this.getItems(form);

    if (!tags[index]) {
      return;
    }

    tags[index][field] = value as never;

    this.setItems(form, tags);
  }

  removeTag(form: FormGroup, index: number): boolean {
    const tags = this.getItems(form);

    tags.splice(index, 1);

    this.setItems(form, tags);

    return !this.hasEmptyTag(tags);
  }

  hasEmptyTag(tags: SpecialCategoryTagItem[]): boolean {
    return tags.some((item) => this.isEmptyTag(item));
  }

  isInvalidTag(
    tags: SpecialCategoryTagItem[],
    index: number,
    submitted: boolean
  ): boolean {
    if (!submitted) {
      return false;
    }

    return this.isEmptyTag(tags[index]);
  }

  getLogCount(
    counts: Record<string, number>,
    item: SpecialCategoryTagItem
  ): number {
    if (!item?.uid) {
      return 0;
    }

    return counts[item.uid] ?? 0;
  }

  getLogSummary(patientId: number): Observable<Record<string, number>> {
    return this.patientService.getSpecialCategoryTagLogSummary(patientId);
  }

  getLogs(
    patientId: number,
    tagUid: string
  ): Observable<SpecialCategoryTagLogView[]> {
    return this.patientService
      .getSpecialCategoryTagLogs(patientId, tagUid)
      .pipe(
        map((response: any) => {
          const logs = this.normalizeLogResponse(response);

          return logs
            .map((log) => this.mapLogToView(log, tagUid))
            .filter((log) => log.changedFields.length > 0);
        })
      );
  }

  getLogDate(log: any): any {
    return log?.createdAt || log?.createDate || log?.date || log?.createdDate;
  }

  getLogUser(log: any): string {
    const user = log?.createdBy || log?.user || log?.createdByUser || null;

    const fullName =
      user?.fullName ||
      `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    return fullName || log?.userName || '-';
  }

  private normalizeLogResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.logs)) {
      return response.logs;
    }

    return [];
  }

  private mapLogToView(
    log: any,
    tagUid?: string
  ): SpecialCategoryTagLogView {
    const oldValue = this.extractTagValue(log?.oldValue, tagUid);
    const newValue = this.extractTagValue(log?.newValue, tagUid);
    const typeClass = this.getLogType(oldValue, newValue);

    return {
      title: this.getLogTitle(log, typeClass),
      action: log?.action ?? '',
      date: this.getLogDate(log),
      userName: this.getLogUser(log),
      badgeText: this.getBadgeText(typeClass),
      typeClass,
      icon: this.getLogIcon(typeClass),
      changedFields: this.getChangedFields(oldValue, newValue),
      raw: log,
    };
  }

  private extractTagValue(value: any, tagUid?: string): any {
    const parsedValue = this.parseMaybeJson(value);

    if (!parsedValue) {
      return null;
    }

    if (Array.isArray(parsedValue)) {
      return parsedValue.find((item) => item?.uid === tagUid) ?? null;
    }

    if (Array.isArray(parsedValue?.specialCategoryTags)) {
      return (
        parsedValue.specialCategoryTags.find(
          (item: any) => item?.uid === tagUid
        ) ?? null
      );
    }

    if (
      parsedValue?.uid ||
      parsedValue?.label !== undefined ||
      parsedValue?.alarm !== undefined ||
      parsedValue?.passive !== undefined
    ) {
      return parsedValue;
    }

    return parsedValue;
  }

  private parseMaybeJson(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);

      /**
       * Bazı durumlarda değer iki kere stringleşmiş gelebilir.
       * Örn: "\"{\\\"uid\\\":\\\"...\\\"}\""
       */
      if (typeof parsed === 'string') {
        return this.parseMaybeJson(parsed);
      }

      return parsed;
    } catch {
      return value;
    }
  }

  private getChangedFields(
    oldValue: any,
    newValue: any
  ): SpecialCategoryTagLogFieldChange[] {
    const oldObj = oldValue || {};
    const newObj = newValue || {};

    const isCreated = !oldValue && !!newValue;
    const isDeleted = !!oldValue && !newValue;

    return this.fieldOrder
      .filter((field) => {
        const oldHasValue = Object.prototype.hasOwnProperty.call(
          oldObj,
          field
        );

        const newHasValue = Object.prototype.hasOwnProperty.call(
          newObj,
          field
        );

        if (!oldHasValue && !newHasValue) {
          return false;
        }

        if (isCreated || isDeleted) {
          return true;
        }

        return !this.isSameFieldValue(field, oldObj[field], newObj[field]);
      })
      .map((field) => ({
        field,
        label: this.fieldLabels[field],
        icon: this.fieldIcons[field],
        oldText: oldValue ? this.formatFieldValue(field, oldObj[field]) : '-',
        newText: newValue ? this.formatFieldValue(field, newObj[field]) : '-',
      }));
  }

  private getLogType(
    oldValue: any,
    newValue: any
  ): 'created' | 'updated' | 'deleted' {
    if (!oldValue && newValue) {
      return 'created';
    }

    if (oldValue && !newValue) {
      return 'deleted';
    }

    return 'updated';
  }

  private getLogTitle(
    log: any,
    type: 'created' | 'updated' | 'deleted'
  ): string {
    if (type === 'created') {
      return 'Etiket Eklendi';
    }

    if (type === 'deleted') {
      return 'Etiket Silindi';
    }

    return log?.label || 'Etiket Güncellendi';
  }

  private getBadgeText(type: 'created' | 'updated' | 'deleted'): string {
    if (type === 'created') {
      return 'Eklendi';
    }

    if (type === 'deleted') {
      return 'Silindi';
    }

    return 'Güncellendi';
  }

  private getLogIcon(type: 'created' | 'updated' | 'deleted'): string {
    if (type === 'created') {
      return 'fa-plus';
    }

    if (type === 'deleted') {
      return 'fa-trash';
    }

    return 'fa-pen';
  }

  private isSameFieldValue(
    field: SpecialCategoryTagLogFieldKey,
    oldValue: any,
    newValue: any
  ): boolean {
    if (field === 'label') {
      return String(oldValue ?? '').trim() === String(newValue ?? '').trim();
    }

    return Boolean(oldValue) === Boolean(newValue);
  }

  private formatFieldValue(
    field: SpecialCategoryTagLogFieldKey,
    value: any
  ): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (field === 'alarm' || field === 'passive') {
      return value ? 'Evet' : 'Hayır';
    }

    return String(value);
  }

  private createUid(): string {
    const cryptoObj = window.crypto as Crypto & {
      randomUUID?: () => string;
    };

    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }

    return `tag_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private setItems(form: FormGroup, tags: SpecialCategoryTagItem[]): void {
    form.get('specialCategoryTags')?.setValue(this.sortTags(tags), {
      emitEvent: false,
    });

    this.touchControl(form);
  }

  private touchControl(form: FormGroup): void {
    form.get('specialCategoryTags')?.markAsDirty();
    form.get('specialCategoryTags')?.markAsTouched();
  }

  private isEmptyTag(item: SpecialCategoryTagItem): boolean {
    return !item?.label || !item.label.toString().trim();
  }
  private sortTags(tags: SpecialCategoryTagItem[]): SpecialCategoryTagItem[] {
    return [...(tags || [])]
      .map((tag, index) => ({ tag, index }))
      .sort((a, b) => {
        const priorityA = this.getTagSortPriority(a.tag);
        const priorityB = this.getTagSortPriority(b.tag);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        /**
         * Aynı öncelikte olanların kendi sırası bozulmasın.
         * Böylece kullanıcı yazı yazarken satırlar gereksiz yer değiştirmez.
         */
        return a.index - b.index;
      })
      .map((item) => item.tag);
  }

  private getTagSortPriority(tag: SpecialCategoryTagItem): number {
    const isAlarm = this.toBoolean(tag?.alarm);
    const isPassive = this.toBoolean(tag?.passive);

    if (!isPassive && isAlarm) {
      return 1;
    }

    if (!isPassive && !isAlarm) {
      return 2;
    }

    if (isPassive && isAlarm) {
      return 3;
    }

    return 4;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

}